import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import CODE from "../../Static/Constants/StatusCodes";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import Loader from "../../Helper/Loaders/Loader";
import CustomDigitalTwinMain from "./DigitalTwin/CustomDigitalTwinMain";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { getBatteryHierarchyV3, getRealTimeInfo } from "../../Api/DigitalTwin";
import Events from "../../Analytics/Events";
import { getPagesMetaData } from "../../Api/Api";
import { useTranslation } from "react-i18next";
import ScreenContainer from "../../Components/ScreenContainer";
import { transformTwinHierarchyData } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import { DigitalTwinProvider } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { getVehicleInfo } from "../../Api/VehicleApi";
import AppCache from "../../Helper/Common/AppCache";

// ─── MetaInfo merge helpers ──────────────────────────────────────────────────

/**
 * Returns a filtered copy of tableConfig whose columnsConfig and columnOrdering
 * contain only the keys listed in allowedKeys (in that order).
 * Falls back to the original config when allowedKeys is absent.
 */
const filterTableColumns = (tableConfig, allowedKeys) => {
  if (!tableConfig || !allowedKeys?.length) return tableConfig;
  const filtered = {};
  allowedKeys.forEach((key) => {
    if (tableConfig.columnsConfig?.[key]) filtered[key] = tableConfig.columnsConfig[key];
  });
  return {
    ...tableConfig,
    columnsConfig: filtered,
    columnOrdering: allowedKeys.filter((k) => tableConfig.columnsConfig?.[k]),
  };
};

/**
 * Merges the API-sourced customDigitalTwin metaInfo with the org-specific
 * config stored in customObservabilityConfig.digitalTwin (AppCache).
 *
 * Org meta fields follow the same top-level format as the page meta:
 *   - tabsToShow        : org meta list overrides page meta list
 *   - batteryKeyMetrics : org meta list overrides page meta list when present
 *   - batteryIndicators : org meta list overrides page meta list when present
 *   - realTimeMatrixes : org meta overrides page meta; used for header metrics
 *   - enabledFeatures  : hardcoded to BMS Faults only for this platform
 *   - bmsFaultsTable   : columns filtered to alerts.columns from org meta
 *
 * Extras forwarded to tab components via context:
 *   - customOverviewSections, customRealTimeMatrixes,
 *     customPerformanceEventTypes, customAlertActions
 */
const buildCustomMetaInfo = (apiMetaInfo, dtConfig) => {
  const overviewConfig = dtConfig.overview || {};
  const alertsConfig = dtConfig.alerts || {};

  // batteryKeyMetrics — org meta list replaces page meta list when provided.
  // Use Array.isArray check so an explicit empty array is respected (not treated as "unset").
  const batteryKeyMetrics = Array.isArray(dtConfig.batteryKeyMetrics)
    ? dtConfig.batteryKeyMetrics
    : apiMetaInfo.batteryKeyMetrics;

  // batteryIndicators — org meta list replaces page meta list when provided.
  // Use Array.isArray check so an explicit empty array is respected (not treated as "unset").
  const batteryIndicators = Array.isArray(dtConfig.batteryIndicators)
    ? dtConfig.batteryIndicators
    : apiMetaInfo.batteryIndicators || [];

  const bmsFaultsTable = filterTableColumns(
    apiMetaInfo.table?.bmsFaultsTable,
    alertsConfig.columns
  );

  return {
    ...apiMetaInfo,
    tabsToShow: dtConfig.tabsToShow || apiMetaInfo.tabsToShow,
    // indicatorProfile — org meta name selects the indicator staleness threshold
    // (resolved server-side); parallel to tabsToShow in each module's config.
    indicatorProfile: dtConfig.indicatorProfile || apiMetaInfo.indicatorProfile,
    batteryKeyMetrics,
    batteryIndicators,
    enabledFeatures: ["DigitalTwin.Alerts.BmsFaults"],
    table: {
      ...apiMetaInfo.table,
      bmsFaultsTable,
    },
    customOverviewSections: overviewConfig.sections || [],
    customRealTimeMatrixes: dtConfig.realTimeMatrixes || apiMetaInfo.realTimeMatrixes || {},
    customPerformanceEventTypes: dtConfig.performance?.eventTypes || [],
    customEventDetailsTable: dtConfig.table?.eventDetailsTable || null,
    customAlertActions: alertsConfig.actions || [],
  };
};

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Custom platform Digital Twin page.
 *
 * Mirrors src/Pages/DigitalTwinV3/DigitalTwin.jsx but builds the metaInfo
 * by merging the API response with customObservabilityConfig.digitalTwin
 * from AppCache, so tab visibility, column lists, and key metrics respect
 * the org's custom platform config rather than the default digitalTwin meta.
 *
 * Route: /custom-observability/specificBattery
 */
function CustomDigitalTwinPage() {
  const queryParams = decryptTheParams();
  const batteryID = queryParams?.batteryID;
  const timezoneChanged = useSelector((state) => state.TimeZoneChanged.value);
  const { t } = useTranslation(["digital_twin"]);

  const [batteryInfo, setBatteryInfo] = useState({});
  const [batteryConfig, setBatteryConfig] = useState({});
  const [batteryStructure, setBatteryStructure] = useState([]);
  const [batterySystem, setBatterySystem] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState({});
  const [batteryInfoFetched, setBatteryInfoFetched] = useState(false);

  const [pagesContent, setPagesContent] = useState({
    batteryID: "",
    partner: "",
    tabValue: 0,
    pageHeader: "",
    mounted: false,
  });

  const fetchDigitalTwinHierarchy = async () => {
    try {
      const [hierarchyRes, realTimeRes, vehicleInfoRes] = await Promise.all([
        getBatteryHierarchyV3(batteryID),
        getRealTimeInfo(batteryID),
        getVehicleInfo(batteryID),
      ]);

      const {
        BATTERY_STRUCTURE: rawStructure,
        batteryConfig: config,
        BATTERY_SYSTEM: rawSystem,
      } = hierarchyRes.response;

      setBatteryInfo(realTimeRes.data || {});
      setBatteryConfig(config || {});
      setBatteryStructure(transformTwinHierarchyData(rawStructure, t));
      setBatterySystem(transformTwinHierarchyData(rawSystem, t));
      setBatteryInfoFetched(true);
      setVehicleInfo(vehicleInfoRes?.response?.data || {});
    } catch (error) {
      console.error("CustomDigitalTwinPage: error fetching hierarchy:", error);
      setBatteryInfoFetched(true);
    }
  };

  const showBusDeviceInfo = () =>
    batteryConfig?.application?.toLowerCase() === "bus";

  const getPageMeta = async () => {
    const res = await getPagesMetaData("customDigitalTwin");
    Events("Custom Digital Twin page opened", { batteryID });

    if (res?.responseStatus?.code === CODE.SUCCESS) {
      const apiMetaInfo = res.response?.response?.metaInfo || {};
      const customObsConfig = AppCache.getItem("customObservabilityConfig") || {};
      const dtConfig = customObsConfig.customDigitalTwin || {};

      setPagesContent((prev) => ({
        ...prev,
        metaInfo: buildCustomMetaInfo(apiMetaInfo, dtConfig),
        mounted: true,
      }));
    }
  };

  useEffect(() => {
    getPageMeta();
  }, []);

  useEffect(() => {
    if (pagesContent.mounted) fetchDigitalTwinHierarchy();
  }, [pagesContent.mounted, timezoneChanged]);

  if (!pagesContent.mounted || !batteryInfoFetched) {
    return (
      <Box sx={{ height: "100vh", bgcolor: ThemeProperties.backgroundPurpleDarker }}>
        <Loader />
      </Box>
    );
  }

  return (
    <ScreenContainer pagesContent={pagesContent} key={timezoneChanged}>
      <DigitalTwinProvider
        value={{
          batteryID,
          batteryInfo,
          setBatteryInfo,
          batteryConfig,
          batteryStructure,
          batterySystem,
          showBusDeviceInfo,
          vehicleInfo,
          setVehicleInfo,
        }}
      >
        <CustomDigitalTwinMain pagesContent={pagesContent} batteryID={batteryID} />
      </DigitalTwinProvider>
    </ScreenContainer>
  );
}

export default CustomDigitalTwinPage;
